import React, { useEffect, useState, useRef, useCallback } from "react";
import { api } from "../lib/api";
import { ChatCircleDots, X, PaperPlaneTilt, CalendarCheck, Heart, Sparkle, CaretLeft, CaretRight, CheckCircle } from "@phosphor-icons/react";

const WD = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const dateKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function ConciergeWidget({ slug }) {
    const [cfg, setCfg] = useState(null);
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [meta, setMeta] = useState({ booking_url: "", cta_url: "", cta_label: "Donate" });
    const [leadOpen, setLeadOpen] = useState(false);
    const [lead, setLead] = useState({ name: "", email: "", phone: "" });
    const [leadDone, setLeadDone] = useState(false);
    const [booking, setBooking] = useState(false);
    const sessionRef = useRef(localStorage.getItem(`cf_concierge_${slug}`) || "");
    const scrollRef = useRef(null);

    useEffect(() => {
        if (!slug) return;
        api.get(`/concierge/widget/${slug}`).then(r => {
            if (r.data?.enabled) {
                setCfg(r.data);
                setMessages([{ role: "assistant", content: r.data.greeting }]);
            }
        }).catch(() => { });
    }, [slug]);

    useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, open]);

    const send = useCallback(async (text) => {
        const msg = (text ?? input).trim();
        if (!msg || sending) return;
        setInput("");
        setMessages(m => [...m, { role: "user", content: msg }]);
        setSending(true);
        try {
            const { data } = await api.post("/concierge/chat", { slug, message: msg, session_id: sessionRef.current || undefined });
            sessionRef.current = data.session_id;
            localStorage.setItem(`cf_concierge_${slug}`, data.session_id);
            setMeta({ booking_url: data.booking_url, cta_url: data.cta_url, cta_label: data.cta_label });
            setMessages(m => [...m, { role: "assistant", content: data.reply }]);
        } catch {
            setMessages(m => [...m, { role: "assistant", content: "Sorry, I couldn't reach our team just now. Please try again in a moment." }]);
        }
        setSending(false);
    }, [input, sending, slug]);

    const submitLead = async () => {
        if (!lead.name.trim() || !lead.email.trim()) return;
        try {
            await api.post("/concierge/lead", { slug, session_id: sessionRef.current || "none", ...lead });
            setLeadDone(true); setLeadOpen(false);
            setMessages(m => [...m, { role: "assistant", content: `Thanks ${lead.name.split(" ")[0]}! Someone from our team will be in touch soon. 💛` }]);
        } catch { /* ignore */ }
    };

    if (!cfg) return null;

    const bookingSlug = (meta.booking_url || "").split("/meet/")[1] || "";

    return (
        <>
            {/* Launcher */}
            {!open && (
                <button onClick={() => setOpen(true)} data-testid="concierge-launcher"
                    className="fixed bottom-5 right-5 z-[60] flex items-center gap-2 bg-accent text-white pl-4 pr-5 py-3 rounded-full shadow-xl hover:scale-105 active:scale-95 transition-transform">
                    <ChatCircleDots size={22} weight="fill" />
                    <span className="font-semibold text-sm hidden sm:inline">Ask {cfg.assistant_name}</span>
                </button>
            )}

            {/* Panel */}
            {open && (
                <div className="fixed bottom-5 right-5 z-[60] w-[calc(100vw-2.5rem)] sm:w-[380px] h-[min(580px,80vh)] bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-testid="concierge-panel">
                    <div className="bg-primary text-primary-foreground px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <span className="w-8 h-8 rounded-full bg-accent flex items-center justify-center"><Sparkle size={16} weight="fill" className="text-white" /></span>
                            <div>
                                <p className="font-heading text-base leading-none">{cfg.assistant_name}</p>
                                <p className="text-[11px] opacity-80 mt-0.5">{cfg.org_name} · usually replies instantly</p>
                            </div>
                        </div>
                        <button onClick={() => setOpen(false)} className="opacity-80 hover:opacity-100" data-testid="concierge-close"><X size={20} /></button>
                    </div>

                    <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-secondary/20">
                        {messages.map((m, i) => (
                            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`concierge-msg-${m.role}`}>
                                <div className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-accent text-white rounded-br-sm" : "bg-card border border-border text-foreground rounded-bl-sm"}`}>{m.content}</div>
                            </div>
                        ))}
                        {sending && <div className="flex justify-start"><div className="bg-card border border-border px-3.5 py-2.5 rounded-2xl rounded-bl-sm text-sm text-muted-foreground" data-testid="concierge-typing">…</div></div>}

                        {/* action chips */}
                        {!sending && messages.length > 1 && !booking && (
                            <div className="flex flex-wrap gap-2 pt-1">
                                {bookingSlug && <button onClick={() => setBooking(true)} className="text-xs inline-flex items-center gap-1 border border-accent text-accent rounded-full px-3 py-1.5 hover:bg-accent hover:text-white transition-colors" data-testid="concierge-book"><CalendarCheck size={13} /> Book a call</button>}
                                {meta.cta_url && <a href={meta.cta_url} target="_blank" rel="noopener noreferrer" className="text-xs inline-flex items-center gap-1 border border-border rounded-full px-3 py-1.5 hover:border-accent transition-colors" data-testid="concierge-cta"><Heart size={13} weight="fill" className="text-rose-500" /> {meta.cta_label}</a>}
                                {!leadDone && <button onClick={() => setLeadOpen(v => !v)} className="text-xs inline-flex items-center gap-1 border border-border rounded-full px-3 py-1.5 hover:border-accent transition-colors" data-testid="concierge-lead-toggle">Talk to our team</button>}
                            </div>
                        )}

                        {/* in-chat booking */}
                        {booking && bookingSlug && (
                            <InlineBooking slug={bookingSlug} prefill={lead}
                                onCancel={() => setBooking(false)}
                                onBooked={(when) => { setBooking(false); setMessages(m => [...m, { role: "assistant", content: `You're all set — your call is booked for ${when}. We just emailed you a calendar invite. See you then! 🎉` }]); }} />
                        )}

                        {leadOpen && !leadDone && (
                            <div className="bg-card border border-border rounded-xl p-3 space-y-2" data-testid="concierge-lead-form">
                                <p className="text-xs text-muted-foreground">Leave your details and our team will follow up.</p>
                                <input className="w-full border border-border px-2.5 py-1.5 text-sm rounded" placeholder="Your name" value={lead.name} onChange={e => setLead({ ...lead, name: e.target.value })} data-testid="concierge-lead-name" />
                                <input className="w-full border border-border px-2.5 py-1.5 text-sm rounded" placeholder="Email" value={lead.email} onChange={e => setLead({ ...lead, email: e.target.value })} data-testid="concierge-lead-email" />
                                <button onClick={submitLead} className="btn-accent w-full justify-center text-sm py-1.5" data-testid="concierge-lead-submit">Send</button>
                            </div>
                        )}
                    </div>

                    <div className="p-3 border-t border-border flex items-center gap-2">
                        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") send(); }}
                            placeholder="Type your question…" className="flex-1 border border-border rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent" data-testid="concierge-input" />
                        <button onClick={() => send()} disabled={sending || !input.trim()} className="w-9 h-9 rounded-full bg-accent text-white flex items-center justify-center disabled:opacity-40" data-testid="concierge-send"><PaperPlaneTilt size={16} weight="fill" /></button>
                    </div>
                </div>
            )}
        </>
    );
}

function InlineBooking({ slug, prefill, onCancel, onBooked }) {
    const [page, setPage] = useState(null);
    const [weekStart, setWeekStart] = useState(0);
    const [selDate, setSelDate] = useState(null);
    const [slots, setSlots] = useState(null);
    const [slot, setSlot] = useState(null);
    const [form, setForm] = useState({ donor_name: prefill?.name || "", donor_email: prefill?.email || "", donor_phone: prefill?.phone || "" });
    const [busy, setBusy] = useState(false);
    const [errMsg, setErrMsg] = useState("");

    useEffect(() => { api.get(`/scheduler/page/${slug}`).then(r => setPage(r.data)).catch(() => setPage(false)); }, [slug]);

    const pickDate = async (d) => {
        setSelDate(d); setSlot(null); setSlots(null);
        try { setSlots((await api.get(`/scheduler/page/${slug}/slots`, { params: { date: dateKey(d) } })).data.slots); }
        catch { setSlots([]); }
    };
    const confirm = async () => {
        if (!form.donor_name.trim() || !form.donor_email.trim()) { setErrMsg("Name and email required"); return; }
        setBusy(true); setErrMsg("");
        try {
            const { data } = await api.post(`/scheduler/page/${slug}/book`, { ...form, start: slot.start });
            onBooked(data.when);
        } catch (e) {
            setErrMsg(e?.response?.data?.detail || "Booking failed");
            if (e?.response?.status === 409) pickDate(selDate);
        }
        setBusy(false);
    };

    if (page === false) return null;
    if (!page) return <div className="bg-card border border-border rounded-xl p-3 text-sm text-muted-foreground" data-testid="inline-booking">Loading times…</div>;

    const today = new Date(); today.setHours(0, 0, 0, 0);
    const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(today); d.setDate(today.getDate() + weekStart + i); return d; });

    return (
        <div className="bg-card border border-accent/40 rounded-xl p-3 space-y-2" data-testid="inline-booking">
            <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-primary flex items-center gap-1.5"><CalendarCheck size={15} className="text-accent" /> Book a {page.duration_min}-min call</p>
                <button onClick={onCancel} className="text-muted-foreground hover:text-foreground" data-testid="inline-booking-cancel"><X size={15} /></button>
            </div>
            {/* date strip */}
            <div className="flex items-center justify-between">
                <button onClick={() => setWeekStart(Math.max(0, weekStart - 7))} disabled={weekStart === 0} className="p-1 disabled:opacity-30"><CaretLeft size={15} /></button>
                <span className="text-[11px] text-muted-foreground">{page.timezone.split("/").pop().replace("_", " ")} time</span>
                <button onClick={() => setWeekStart(weekStart + 7)} className="p-1"><CaretRight size={15} /></button>
            </div>
            <div className="grid grid-cols-7 gap-1">
                {days.map(d => {
                    const avail = page.available_weekdays.includes(WD[d.getDay()]);
                    const isSel = selDate && dateKey(d) === dateKey(selDate);
                    return (
                        <button key={dateKey(d)} disabled={!avail} onClick={() => pickDate(d)} data-testid={`inline-day-${dateKey(d)}`}
                            className={`rounded py-1 text-center text-[11px] ${isSel ? "bg-accent text-white" : avail ? "hover:bg-secondary border border-border" : "opacity-25"}`}>
                            <div className="uppercase font-bold">{d.toLocaleDateString("en-US", { weekday: "short" }).slice(0, 2)}</div>
                            <div className="font-heading text-sm">{d.getDate()}</div>
                        </button>
                    );
                })}
            </div>
            {/* slots */}
            {selDate && (slots === null ? <p className="text-xs text-muted-foreground text-center py-2">Loading…</p>
                : slots.length === 0 ? <p className="text-xs text-muted-foreground text-center py-2" data-testid="inline-no-slots">No times this day.</p>
                    : !slot ? (
                        <div className="grid grid-cols-3 gap-1.5 max-h-28 overflow-y-auto">
                            {slots.map(s => <button key={s.start} onClick={() => setSlot(s)} data-testid={`inline-slot-${s.start}`} className="py-1.5 rounded text-xs font-semibold border border-border hover:border-accent">{s.label}</button>)}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <p className="text-xs text-primary font-semibold flex items-center gap-1"><CheckCircle size={13} className="text-accent" weight="fill" /> {selDate.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })} at {slot.label}</p>
                            <input className="w-full border border-border px-2.5 py-1.5 text-sm rounded" placeholder="Your name" value={form.donor_name} onChange={e => setForm({ ...form, donor_name: e.target.value })} data-testid="inline-name" />
                            <input className="w-full border border-border px-2.5 py-1.5 text-sm rounded" placeholder="Email" value={form.donor_email} onChange={e => setForm({ ...form, donor_email: e.target.value })} data-testid="inline-email" />
                            {errMsg && <p className="text-xs text-red-600">{errMsg}</p>}
                            <button onClick={confirm} disabled={busy} className="btn-accent w-full justify-center text-sm py-1.5" data-testid="inline-confirm">{busy ? "Booking…" : "Confirm booking"}</button>
                        </div>
                    ))}
        </div>
    );
}
